/**
 * Settings > Clients
 *
 * CRUD for external client tenants. Each client has a name + optional
 * contact info + logo. Users with this client_id will see the logo in
 * their sidebar footer.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clientsApi } from '../../../api/client';
import HelpButton from '../../help/HelpButton';

export default function ClientsPage() {
  const qc = useQueryClient();
  const { data: clients = [], isLoading } = useQuery({
    queryKey: ['admin', 'clients'],
    queryFn: () => clientsApi.list(),
  });

  const [editing, setEditing] = useState(null);   // client object or { __new: true }
  const [confirmDelete, setConfirmDelete] = useState(null);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">Clients</h2>
          <p className="text-sm text-surface-muted dark:text-surface-dark-muted">
            External tenants. Assign users to a client in the Users tab.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton moduleKey="settings_clients" />
          <button
            onClick={() => setEditing({ __new: true })}
            className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 text-white text-sm"
          >
            + New client
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-surface-muted">Loading…</div>
      ) : (
        <div className="border border-surface-border dark:border-surface-dark-border rounded-thin overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Logo</th>
                <th className="px-3 py-2 text-left font-medium">Name</th>
                <th className="px-3 py-2 text-left font-medium">Code</th>
                <th className="px-3 py-2 text-left font-medium">Contact</th>
                <th className="px-3 py-2 text-left font-medium">Users</th>
                <th className="px-3 py-2 text-left font-medium">SR Bypass</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-surface-muted dark:text-surface-dark-muted">
                    No clients yet. Click <em>+ New client</em> to add one.
                  </td>
                </tr>
              )}
              {clients.map((c) => (
                <tr key={c.id} className="border-t border-surface-border dark:border-surface-dark-border">
                  <td className="px-3 py-2">
                    {c.has_logo ? (
                      <img src={c.logo_url} alt={c.name} className="h-8 w-auto max-w-[80px] object-contain" />
                    ) : (
                      <span className="text-xs text-surface-muted">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-medium">{c.name}</td>
                  <td className="px-3 py-2">{c.short_code || '—'}</td>
                  <td className="px-3 py-2">{c.contact_email || c.contact_phone || '—'}</td>
                  <td className="px-3 py-2">{c.user_count}</td>
                  <td className="px-3 py-2">
                    {c.screen_recording_bypass_allowed ? (
                      <span className="text-amber-600 dark:text-amber-400" title="Agents may proceed unrecorded if Azure connectivity fails">⚠️ On</span>
                    ) : (
                      <span className="text-surface-muted">Off</span>
                    )}
                  </td>
                  <td className="px-3 py-2 capitalize">{c.status}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setEditing(c)} className="text-continuum-600 dark:text-continuum-200 text-sm mr-3">Edit</button>
                    <button onClick={() => setConfirmDelete(c)} className="text-red-600 text-sm">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ClientEditModal
          client={editing.__new ? null : editing}
          onClose={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['admin', 'clients'] }); }}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          client={confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onDone={() => { setConfirmDelete(null); qc.invalidateQueries({ queryKey: ['admin', 'clients'] }); }}
        />
      )}
    </div>
  );
}


function ClientEditModal({ client, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(() => ({
    name: client?.name || '',
    short_code: client?.short_code || '',
    contact_email: client?.contact_email || '',
    contact_phone: client?.contact_phone || '',
    notes: client?.notes || '',
    screen_recording_bypass_allowed: !!client?.screen_recording_bypass_allowed,
  }));
  const [logoFile, setLogoFile] = useState(null);
  const [error, setError] = useState(null);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name.trim(),
        short_code: form.short_code.trim() || null,
        contact_email: form.contact_email.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        notes: form.notes.trim() || null,
        screen_recording_bypass_allowed: !!form.screen_recording_bypass_allowed,
      };
      const saved = client ? await clientsApi.update(client.id, payload) : await clientsApi.create(payload);
      if (logoFile) {
        await clientsApi.uploadLogo(saved.id, logoFile);
      }
      return saved;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin', 'clients'] }); onClose(); },
    onError: (e) => setError(e.detail || e.message),
  });

  const removeLogo = useMutation({
    mutationFn: () => clientsApi.removeLogo(client.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'clients'] }),
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-lg bg-surface-panel dark:bg-surface-dark-panel border border-surface-border dark:border-surface-dark-border rounded-thin shadow-lg">
        <div className="px-4 py-3 border-b border-surface-border dark:border-surface-dark-border flex items-center justify-between">
          <h3 className="font-semibold">{client ? `Edit client: ${client.name}` : 'New client'}</h3>
          <button onClick={onClose} className="text-surface-muted hover:text-surface-text">✕</button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          {error && <div className="text-red-600 text-sm">{String(error)}</div>}

          <Field label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Short code" value={form.short_code} onChange={(v) => setForm({ ...form, short_code: v })} />
          <Field label="Contact email" type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} />
          <Field label="Contact phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} />
          <div>
            <label className="block text-xs text-surface-muted dark:text-surface-dark-muted mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin text-sm"
            />
          </div>

          <div className="border-t border-surface-border dark:border-surface-dark-border pt-3 mt-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.screen_recording_bypass_allowed}
                onChange={(e) => setForm({ ...form, screen_recording_bypass_allowed: e.target.checked })}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium">Allow login without screen recording</span>
                <span className="block text-xs text-surface-muted dark:text-surface-dark-muted">
                  If Azure screen-recording connectivity fails at login, agents in this
                  tenant may choose to proceed <strong>unrecorded</strong> (logged for
                  compliance). When OFF, those agents are blocked from logging in until
                  connectivity is restored.
                </span>
              </span>
            </label>
          </div>

          <div className="border-t border-surface-border dark:border-surface-dark-border pt-3 mt-3">
            <label className="block text-xs text-surface-muted dark:text-surface-dark-muted mb-1">Logo (PNG/JPG/SVG/WebP, ≤2 MB)</label>
            {client?.has_logo && (
              <div className="flex items-center gap-3 mb-2">
                <img src={client.logo_url} alt="" className="h-12 w-auto object-contain" />
                <button onClick={() => removeLogo.mutate()} className="text-red-600 text-xs">Remove current</button>
              </div>
            )}
            <input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
              className="text-sm"
            />
            {logoFile && <div className="text-xs text-surface-muted mt-1">Will upload: {logoFile.name}</div>}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-surface-border dark:border-surface-dark-border flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm">Cancel</button>
          <button
            disabled={!form.name.trim() || save.isPending}
            onClick={() => save.mutate()}
            className="px-3 py-1.5 rounded-thin bg-continuum-500 hover:bg-continuum-600 disabled:opacity-40 text-white text-sm"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ client, onClose, onDone }) {
  const remove = useMutation({
    mutationFn: () => clientsApi.remove(client.id),
    onSuccess: onDone,
  });
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-md bg-surface-panel dark:bg-surface-dark-panel border border-surface-border dark:border-surface-dark-border rounded-thin shadow-lg">
        <div className="p-4">
          <h3 className="font-semibold mb-2">Delete client</h3>
          <p className="text-sm text-surface-muted dark:text-surface-dark-muted">
            Are you sure you want to delete <strong>{client.name}</strong>? This cannot be undone.
            {client.user_count > 0 && (
              <> Currently <strong>{client.user_count}</strong> user(s) are assigned to this client — reassign or remove them first.</>
            )}
          </p>
          {remove.error && <div className="text-red-600 text-sm mt-2">{String(remove.error.detail || remove.error.message)}</div>}
        </div>
        <div className="px-4 py-3 border-t border-surface-border dark:border-surface-dark-border flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm">Cancel</button>
          <button
            disabled={client.user_count > 0 || remove.isPending}
            onClick={() => remove.mutate()}
            className="px-3 py-1.5 rounded-thin bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-sm"
          >
            {remove.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs text-surface-muted dark:text-surface-dark-muted mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin text-sm"
      />
    </div>
  );
}
